import { FC, memo, useEffect, useState } from 'react';
import { Form, message, Typography } from 'antd';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { FormItem, SubmitButton, TextField } from 'componets/form';
import { Loading, Paper } from 'componets/ui';
import { RegistrationLayout } from 'componets/layouts';

import { Rules } from 'utils/constants';
import Utils from 'utils/utils';

import { FieldData } from 'models';

import { checkResetPasswordToken, resetPassword } from 'features/forgotPassword/actions';

import { useAppDispatch, useTranslate } from 'hooks';

import s from './index.module.scss';

import { FORGOT_PASSWORD } from 'utils/constants/Paths';

const PASSWORD_LENGTH = 8;

const PASSWORD_RULES = [Rules.REQUIRED, Rules.LOWERCASE, Rules.UPPERCASE, Rules.NUMERIC];

const ForgotPasswordResetPassword: FC = memo(() => {
	const {
		passwordResetText,
		newPasswordText,
		repeatNewPasswordText,
		saveText,
		changePasswordFormText,
		linkExpiredText,
	} = useTranslate([
		'passwordResetText',
		'newPasswordText',
		'repeatNewPasswordText',
		'changePasswordFormText',
		'saveText',
		'linkExpiredText',
	]);

	const navigate = useNavigate();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [invalidMessage, setInvalidMessage] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const dispatch = useAppDispatch();

	const { token } = useParams();

	const onFinish = (data: any) => {
		setIsSubmitting(true);
		dispatch(resetPassword({ ...data, token }))
			.unwrap()
			.then(() => {
				navigate(FORGOT_PASSWORD);
			})
			.catch(({ errors }) => {
				if (errors.token) {
					message.warn(errors.token[0]);
				}
				Utils.injectFormErrors(fields, errors, setFields);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	useEffect(() => {
		if (token) {
			dispatch(checkResetPasswordToken(token))
				.unwrap()
				.catch((e) => {
					setInvalidMessage(linkExpiredText);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [dispatch, token]);

	if (isLoading) return <Loading />;

	return (
		<RegistrationLayout>
			<Paper styleName={s.forgotPassword}>
				<Typography.Title className={s.title} level={4}>
					{passwordResetText}
				</Typography.Title>
				{invalidMessage && <Typography.Text type="danger">{invalidMessage}</Typography.Text>}
				<Typography.Text>{changePasswordFormText}</Typography.Text>
				<Form onFinish={onFinish} onFieldsChange={setFields} fields={fields}>
					<FormItem
						rules={[...PASSWORD_RULES, Rules.MIN_LENGTH('password', PASSWORD_LENGTH)]}
						name="password"
					>
						<TextField label={newPasswordText} type="password" />
					</FormItem>

					<FormItem
						rules={[
							...PASSWORD_RULES,
							Rules.MIN_LENGTH('repeatPassword', PASSWORD_LENGTH),
							Rules.COMPARE_PASSWORDS('password'),
						]}
						name="repeatPassword"
					>
						<TextField label={repeatNewPasswordText} type="password" />
					</FormItem>

					<SubmitButton text={saveText} loading={isSubmitting} />
				</Form>
			</Paper>
		</RegistrationLayout>
	);
});

export { ForgotPasswordResetPassword };
